import { useEffect, useState } from "react";
import { UserPlus, Loader2, CheckCircle2, Smartphone, ChevronDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * ManualReferralForm — Vermittler erfasst eine Empfehlung manuell.
 *
 * Simple text inputs for Vorname / Nachname / Handy / E-Mail (+ optional Note).
 * Submits to POST /api/vermittler/referrals; admin reviews and (eventually)
 * awards points when the lead converts to a sale.
 */
export default function ManualReferralForm({ highlight = false }) {
    const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", city: "", note: "", mobile_app_enabled: false });
    const [submitting, setSubmitting] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const loadHistory = async () => {
        try {
            const res = await api.get("/vermittler/referrals");
            setHistory(res.data.items || []);
        } catch {
            /* offline */
        } finally {
            setLoadingHistory(false);
        }
    };
    useEffect(() => { loadHistory(); }, []);

    const submit = async () => {
        // Mai-2026 (User-Wunsch 21 May): erweiterte Pflichtfeld-Validierung
        // - Vorname ODER Nachname (mind. 1)
        // - Handynummer ODER E-Mail (mind. 1)
        // - Stadt: Pflicht
        // - Interesse an (vorher: Notiz optional): Pflicht
        const hasName = form.first_name.trim() || form.last_name.trim();
        const hasContact = form.phone.trim() || form.email.trim();
        if (!hasName) {
            toast.error("Bitte Vorname oder Nachname angeben");
            return;
        }
        if (!hasContact) {
            toast.error("Bitte Handynummer oder E-Mail angeben");
            return;
        }
        if (!form.city.trim()) {
            toast.error("Bitte Stadt angeben");
            return;
        }
        if (!form.note.trim()) {
            toast.error("Bitte Interesse an angeben");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/vermittler/referrals", form);
            toast.success("Empfehlung übermittelt — das Team meldet sich bei Kontakt-Aufnahme");
            setForm({ first_name: "", last_name: "", phone: "", email: "", city: "", note: "", mobile_app_enabled: form.mobile_app_enabled });
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Übermittlung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`bg-white rounded-sm ${highlight ? "border-2 border-[#FFCC00] shadow-md ring-1 ring-[#FFCC00]/30" : "border border-gray-200"}`} data-testid="manual-referral-form">
            <button
                type="button"
                onClick={() => setFormOpen((o) => !o)}
                className={`w-full flex items-center gap-3 p-5 text-left transition-colors ${highlight ? "bg-[#FFCC00]/10 hover:bg-[#FFCC00]/20" : "hover:bg-gray-50"}`}
                aria-expanded={formOpen}
                data-testid="manual-referral-toggle"
            >
                <UserPlus className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-base">Empfehlung erfassen</h3>
                        {highlight && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm" data-testid="manual-referral-highlight-badge">
                                <Sparkles className="w-3 h-3" /> Punkte sichern
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                        {formOpen
                            ? "Sie haben einem Kunden TwoWheels empfohlen? Tragen Sie hier seine Kontaktdaten ein — wir nehmen Kontakt auf."
                            : `${history.length} Empfehlung${history.length === 1 ? "" : "en"} übermittelt — Klicken zum Erfassen`}
                    </p>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${formOpen ? "rotate-180" : ""}`} />
            </button>
            {formOpen && (
            <div className="px-6 pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label htmlFor="ref-fn" className="text-[11px] uppercase tracking-wider font-bold">Vorname <span className="text-gray-400 normal-case font-normal">(wahlweise *)</span></Label>
                    <Input
                        id="ref-fn"
                        value={form.first_name}
                        onChange={(e) => update("first_name", e.target.value)}
                        placeholder="z. B. Max"
                        className="rounded-sm"
                        data-testid="manual-referral-first-name"
                    />
                </div>
                <div>
                    <Label htmlFor="ref-ln" className="text-[11px] uppercase tracking-wider font-bold">Nachname <span className="text-gray-400 normal-case font-normal">(wahlweise *)</span></Label>
                    <Input
                        id="ref-ln"
                        value={form.last_name}
                        onChange={(e) => update("last_name", e.target.value)}
                        placeholder="z. B. Mustermann"
                        className="rounded-sm"
                        data-testid="manual-referral-last-name"
                    />
                </div>
                <div>
                    <Label htmlFor="ref-phone" className="text-[11px] uppercase tracking-wider font-bold">Handynummer <span className="text-gray-400 normal-case font-normal">(wahlweise *)</span></Label>
                    <Input
                        id="ref-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+49 170 1234567"
                        inputMode="tel"
                        className="rounded-sm"
                        data-testid="manual-referral-phone"
                    />
                </div>
                <div>
                    <Label htmlFor="ref-email" className="text-[11px] uppercase tracking-wider font-bold">E-Mail <span className="text-gray-400 normal-case font-normal">(wahlweise *)</span></Label>
                    <Input
                        id="ref-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="max@example.com"
                        className="rounded-sm"
                        data-testid="manual-referral-email"
                    />
                </div>
                <div className="sm:col-span-2">
                    <Label htmlFor="ref-city" className="text-[11px] uppercase tracking-wider font-bold">Stadt *</Label>
                    <Input
                        id="ref-city"
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        placeholder="z. B. München"
                        className="rounded-sm"
                        data-testid="manual-referral-city"
                    />
                </div>
            </div>
            <div className="mt-3">
                <Label htmlFor="ref-note" className="text-[11px] uppercase tracking-wider font-bold">Interesse an *</Label>
                <Textarea
                    id="ref-note"
                    value={form.note}
                    onChange={(e) => update("note", e.target.value)}
                    rows={2}
                    placeholder="z. B. Vespa GTS 300, Budget 5.000 €"
                    className="rounded-sm text-sm"
                    data-testid="manual-referral-note"
                />
            </div>
            <label className="mt-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-sm cursor-pointer" data-testid="manual-referral-mobile-app-toggle">
                <input
                    type="checkbox"
                    checked={form.mobile_app_enabled}
                    onChange={(e) => update("mobile_app_enabled", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#FFCC00]"
                    data-testid="manual-referral-mobile-app-checkbox"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-1.5 font-bold text-sm">
                        <Smartphone className="w-4 h-4" /> Auch in mobiler App nutzbar
                    </div>
                    <div className="text-[11px] text-gray-600 mt-0.5">
                        Empfehlung kann auch über die TwoWheels-Mobile-App eingereicht werden (Kontakt-Auswahl aus Adressbuch).
                    </div>
                </div>
            </label>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-[11px] text-gray-500">
                    Pflicht: (Vorname ODER Nachname) + (Handynummer ODER E-Mail) + Stadt + Interesse an. Die Daten werden DSGVO-konform behandelt.
                </p>
                <Button
                    onClick={submit}
                    disabled={submitting}
                    className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                    data-testid="manual-referral-submit"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Empfehlung übermitteln"}
                </Button>
            </div>
            </div>
            )}

            {/* Compact submitted history (no infinite scroll) */}
            <div className="px-6 pb-6 pt-4 border-t border-gray-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                    Übermittelte Empfehlungen
                </div>
                {loadingHistory ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : history.length === 0 ? (
                    <div className="text-xs text-gray-500" data-testid="manual-referral-empty">
                        Noch keine Empfehlungen übermittelt.
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-100 max-h-48 overflow-auto" data-testid="manual-referral-history">
                        {history.slice(0, 10).map((r) => (
                            <li key={r.id} className="py-2 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-[#111827] truncate">
                                        {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                                    </div>
                                    <div className="text-[11px] text-gray-500 truncate">
                                        {[r.email, r.phone, r.city].filter(Boolean).join(" · ") || "—"}
                                    </div>
                                    <div className="text-[10px] text-gray-400 mt-0.5 font-mono-num">
                                        {new Date(r.created_at).toLocaleString("de-DE")}
                                    </div>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 shrink-0">
                                    {r.status === "submitted" ? "In Prüfung" : r.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
